import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  StyleSheet,
  ImageBackground,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from "react-native";
import { translateText } from "../translations/translationService";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  loginAuthenticate,
  loginWithLinkPayload,
  UserAuthQuality,
  setLog,
  fullDataPiece,
} from "../services/ConnectDataBase";
import { getArrayInfoDataUpdate } from "../services/Functions";
import { useDispatch } from "react-redux";
import {
  setLang,
  setName,
  setTeam,
  setUserType,
  setCodTeam,
  setAuthQualityUsers,
  setNivelAcceso,
  setCalendario,
  updateInfoData,
} from "../redux/reducers";
import texts from "../translations/texts";
import { getLanguage } from "../services/Functions";
import { DEPARTAMENTS, backgroundColor } from "../constants/Constants";
import { LOGIN_SCREEN_COLORS } from "../style/colors.js";
import { SCREEN_IDS } from "../constants/Constants";
import {
  loginAuthenticateWithToken,
  fetchCalendarioLaboral,
} from "../services/ConnectDataBase";
import DeviceInfo from "react-native-device-info";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { getErrorMessage, notify } from "../services/ui/notify";

// Configuración centralizada
const LOGIN_CONFIG = {
  EXCEPTION_USERS: ["p.herrera@facet.es"],
  IMAGE_URL: "https://new.facetbarcelona.com/static/media/fondoJTS.jpg",
  LOGO_SIZES: {
    large: { width: 170, height: 100 },
    normal: { width: 150, height: 75 }
  }
};

// Función para generar UUID
const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Obtener o generar UUID en web o móvil
const getOrCreateUUID = async () => {
  let uuid = "";

  if (typeof navigator !== "undefined" && navigator.userAgent) {
    // WEB: usar localStorage
    uuid = localStorage.getItem("deviceUUID");
    if (!uuid) {
      uuid = generateUUID();
      localStorage.setItem("deviceUUID", uuid);
    }
  } else {
    // MÓVIL: usar AsyncStorage
    uuid = await AsyncStorage.getItem("deviceUUID");
    if (!uuid) {
      uuid = generateUUID();
      await AsyncStorage.setItem("deviceUUID", uuid);
    }
  }

  return uuid;
};

const detectBrowserLanguage = () => {
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language.split("-")[0]; 
  }
  return "es"; // Idioma predeterminado
};

const getCookieValue = (cookieName) => {
  if (typeof document !== "undefined") {
    const cookies = document.cookie.split("; ");
    const cookie = cookies.find((row) => row.startsWith(`${cookieName}=`));
    return cookie ? cookie.split("=")[1] : null;
  }
  return null;
};

// Función para validar campos de entrada
const validateInputFields = (username, password) => {
  if (!username.trim()) {
    return { isValid: false, message: "Por favor introduce tu correo electrónico" };
  }
  if (!password.trim()) {
    return { isValid: false, message: "Por favor introduce tu contraseña" };
  }
  if (!username.includes("@")) {
    return { isValid: false, message: "Por favor introduce un correo electrónico válido" };
  }
  return { isValid: true };
};

// Función para obtener la ruta de navegación
const getNavigationRoute = (isAdmin, departmentKey) => {
  if (isAdmin === "S") return "Admin Setting";
  if (["13", "2"].includes(departmentKey)) return "Scan";
  return "Operator Panel";
};

export default function LoginScreen({ navigation }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userLang, setUserLang] = useState(detectBrowserLanguage());
  const [imageReady, setImageReady] = useState(false);
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width
  );
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height
  );
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const dispatch = useDispatch();
  const lang = getLanguage();

  // Memoizar estilos para mejor performance
  const styles = useMemo(
    () => getStyles(screenWidth, screenHeight),
    [screenWidth, screenHeight]
  );

  // Cargar imagen de fondo
  useEffect(() => {
    Image.prefetch(LOGIN_CONFIG.IMAGE_URL)
      .then(() => setImageReady(true))
      .catch(() => setImageReady(true)); // Incluso si falla, se renderiza para evitar bloqueo
  }, []);

  // Manejar cambios de dimensiones de pantalla
  useEffect(() => {
    const updateDimensions = ({ window }) => {
      setScreenWidth(window.width);
      setScreenHeight(window.height);
    };

    const subscription = Dimensions.addEventListener(
      "change",
      updateDimensions
    );

    return () => {
      subscription?.remove();
    };
  }, []);

  // Cargar calendario laboral
  useEffect(() => {
    const obtenerCalendario = async () => {
      try {
        const calendario = await fetchCalendarioLaboral();
        if (calendario) {
          dispatch(setCalendario(calendario));
        }
      } catch (error) {
        console.error("Error al obtener el calendario:", error);
      }
    };

    obtenerCalendario();
  }, [dispatch]);

  // Detectar enlace con payload cifrado (link-login desde correo) — solo web
  useEffect(() => {
    if (typeof window === "undefined" || !window.location) return;

    const getPayloadFromUrl = () => {
      const search = window.location.search || "";
      const hash = window.location.hash || "";
      const fromSearch = search.match(/[?&]d=([^&]+)/);
      const fromHash = hash.match(/[?&]d=([^&]+)/);
      const encoded = (fromSearch && fromSearch[1]) || (fromHash && fromHash[1]);
      return encoded ? decodeURIComponent(encoded) : null;
    };

    const clearPayloadFromUrl = () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("d");
        let cleanPath = url.searchParams.toString()
          ? `${url.pathname}?${url.searchParams.toString()}`
          : url.pathname;
        const hash = window.location.hash || "";
        if (hash.includes("d=")) {
          const hashParts = hash.split("?");
          const baseHash = hashParts[0];
          const params = new URLSearchParams(hashParts[1] || "");
          params.delete("d");
          const newHash = params.toString() ? `${baseHash}?${params}` : baseHash;
          cleanPath = cleanPath + (newHash || "");
        } else if (hash) {
          cleanPath = cleanPath + hash;
        }
        window.history.replaceState({}, "", cleanPath);
      } catch (e) {
        window.history.replaceState({}, "", window.location.pathname);
      }
    };

    const payload = getPayloadFromUrl();
    if (!payload) return;

    clearPayloadFromUrl();

    const runLinkLogin = async () => {
      setIsLoading(true);
      try {
        const response = await loginWithLinkPayload(payload);
        if (!response || response.status !== "ok" || !response.data?.[0]) {
          const msg =
            response?.message ||
            (response?.data?.message) ||
            translateText("Enlace inválido o expirado.", userLang);
          console.warn("[link-login] Respuesta no ok:", response);
          showErrorToast(msg);
          return;
        }

        const user = response.data[0].name;
        if (!LOGIN_CONFIG.EXCEPTION_USERS.includes(user)) {
          sessionStorage.setItem("authToken", response.token);
          sessionStorage.setItem("username", user);
        }

        const listUserAuth = await UsuariosAuthCalidad();
        dispatch(setAuthQualityUsers(listUserAuth || []));

        const mo = response.mo;
        if (mo) {
          const dataOk = await fullDataPiece(mo, 2);
          if (dataOk) {
            const infoData = getArrayInfoDataUpdate(dataOk);
            dispatch(updateInfoData(infoData));
            authenticateOk(response.data[0], { redirectTo: "Info", routeParams: { incidencia: false } });
            sendLoginLog(user);
            return;
          }
        }

        authenticateOk(response.data[0]);
        sendLoginLog(user);
      } catch (error) {
        console.error("Error en login por enlace:", error);
        const isNetwork =
          error.code === "ERR_NETWORK" ||
          error.message === "Network Error" ||
          !error.response;
        const msg = isNetwork
          ? translateText("Error de conexión. Comprueba la red o que el servidor esté disponible.", userLang)
          : (error.response?.data?.message || translateText("Enlace inválido o expirado.", userLang));
        showErrorToast(msg);
      } finally {
        setIsLoading(false);
      }
    };

    runLinkLogin();
  }, [dispatch, userLang, UsuariosAuthCalidad, sendLoginLog]);

  // Validar token existente al cargar
  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const user = sessionStorage.getItem("username");

    if (token && user && !LOGIN_CONFIG.EXCEPTION_USERS.includes(user)) {
      const validateToken = async () => {
        try {
          const response = await loginAuthenticateWithToken(
            token,
            SCREEN_IDS.VALIDATE_TOKEN
          );
          if (response.status === "ok") {
            const cookieLang = getCookieValue("lang");
            if (cookieLang) {
              setUserLang(cookieLang);
            }

            const listUserAuth = await UsuariosAuthCalidad();
            dispatch(setAuthQualityUsers(listUserAuth));
            authenticateOk(response.data[0]);
          } else {
            sessionStorage.removeItem("authToken");
            sessionStorage.removeItem("username");
            console.error("Token inválido o expirado, por favor inicia sesión.");
            showErrorToast(translateText("Tu sesión ha expirado. Inicia sesión de nuevo.", userLang));
          }
        } catch (error) {
          console.error("Error al validar el token", error);
          sessionStorage.removeItem("authToken");
          sessionStorage.removeItem("username");
          const msg = getErrorMessage(
            error,
            translateText("No se pudo validar la sesión. Inicia sesión de nuevo.", userLang)
          );
          showErrorToast(msg);
        }
      };
      validateToken();
    }
  }, [dispatch, authenticateOk, UsuariosAuthCalidad, showErrorToast, userLang]);

  // Memoizar función para obtener usuarios de calidad
  const UsuariosAuthCalidad = useCallback(async () => {
    try {
      const listUserAuth = await UserAuthQuality(SCREEN_IDS.GENERAL);
      return listUserAuth;
    } catch (error) {
      console.error("Error al obtener usuarios de calidad:", error);
      return [];
    }
  }, []);

  // Función para enviar logs de seguridad
  const sendLoginLog = useCallback(async (username) => {
    try {
      let deviceInfo = "";
      const uuid = await getOrCreateUUID();

      if (typeof navigator !== "undefined" && navigator.userAgent) {
        const userAgent = navigator.userAgent;
        const platform = navigator.platform
          ? navigator.platform
          : "Unknown Platform";
        deviceInfo = `User Agent: ${userAgent}, Platform: ${platform}, UUID: ${uuid}`;
      } else {
        const deviceName = await DeviceInfo.getDeviceName();
        const deviceModel = DeviceInfo.getModel();
        const uniqueId = await DeviceInfo.getUniqueId();
        const systemName = DeviceInfo.getSystemName();
        const systemVersion = DeviceInfo.getSystemVersion();

        deviceInfo = `Device Name: ${deviceName}, Model: ${deviceModel}, OS: ${systemName} ${systemVersion}, UUID: ${uuid}`;
      }

      const logData = `User ${username} logged in from device: ${deviceInfo}`;
      await setLog(SCREEN_IDS.GENERAL, "LOG", "Security", logData);
    } catch (error) {
      console.error("Error al obtener la información del dispositivo:", error);
    }
  }, []);

  // Función para mostrar errores de forma consistente
  const showErrorToast = useCallback((message, subMessage = null) => {
    notify.error(message, {
      description:
        subMessage || translateText("Inténtalo nuevamente.", userLang),
    });
  }, [userLang]);

  // Función principal de validación de credenciales
  const validateCredentials = useCallback(async (username, password) => {
    try {
      // Validar campos de entrada
      const validation = validateInputFields(username, password);
      if (!validation.isValid) {
        showErrorToast(validation.message);
        return;
      }

      setIsLoading(true);

      // Enviar la contraseña al servidor
      const authenticate = await loginAuthenticate(username, password);
      
      if (authenticate.status === "ok") {
        setIsLoading(false);

        // Guardar el token y el usuario en sessionStorage si NO está en la lista de excepciones
        if (!LOGIN_CONFIG.EXCEPTION_USERS.includes(username)) {
          sessionStorage.setItem("authToken", authenticate.token);
          sessionStorage.setItem("username", username);
        }

        // Cargar los usuarios autorizados de calidad
        const listUserAuth = await UsuariosAuthCalidad();
        dispatch(setAuthQualityUsers(listUserAuth));

        // Autenticar al usuario
        authenticateOk(authenticate.data[0]);
        sendLoginLog(username);
      } else {
        console.error("Error de autenticación:", authenticate.message);
        showErrorToast(
          translateText("Usuario o contraseña incorrectos.", userLang)
        );
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error en autenticación:", error);
      
      // Mostrar mensaje de error más específico
      const errorMessage = getErrorMessage(
        error,
        translateText(
          "Error de conexión. Verifica tu conexión a internet.",
          userLang
        )
      );
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, userLang, showErrorToast, UsuariosAuthCalidad, sendLoginLog]);

  // Función para manejar la autenticación exitosa
  // opts.redirectTo: si se indica (ej. "Info"), navega ahí en lugar de la ruta por defecto (link-login)
  const authenticateOk = useCallback((data, opts) => {
    const dataUser = data;
    dispatch(setName(dataUser.name));
    dispatch(setTeam(dataUser.team));
    dispatch(setCodTeam(dataUser.cod_team));
    dispatch(setNivelAcceso(dataUser.permisos));
    
    const isAdmin = dataUser.admin === "S" ? "S" : "N";
    const selectedLang = lang === dataUser.lang ? lang : dataUser.lang;
    
    setUserLang(selectedLang);
    dispatch(setLang(selectedLang));
    dispatch(setUserType(isAdmin));
    
    if (opts?.redirectTo) {
      navigation.navigate(opts.redirectTo, opts.routeParams || {});
      return;
    }
    
    const team = dataUser.team;
    let departmentKey;

    for (const key in DEPARTAMENTS) {
      if (DEPARTAMENTS[key] === team) {
        departmentKey = key;
        break;
      }
    }

    // Navegar a la pantalla correspondiente
    const route = getNavigationRoute(isAdmin, departmentKey);
    navigation.navigate(route);
  }, [dispatch, lang, navigation]);

  // Función para manejar el envío del formulario
  const handleSubmit = useCallback(() => {
    if (isLoading) return;
    validateCredentials(username, password);
  }, [username, password, isLoading, validateCredentials]);

  if (!imageReady) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size='large' color={LOGIN_SCREEN_COLORS.ACTIVITY_INDICATOR} />
      </View>
    );
  }

  return (
    <ImageBackground
      source={{
        uri: LOGIN_CONFIG.IMAGE_URL,
      }}
      style={styles.background}>
      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === "ios" ? "padding" : "height"}>
        <ScrollView
          contentContainerStyle={styles.scrollViewContent}
          keyboardShouldPersistTaps='handled'>
          <View style={styles.loginContainer}>
            <View style={styles.logoContainer}>
              <Image
                source={require("../assets/logoFacet.png")}
                style={styles.logo}
                resizeMode='contain'
                accessibilityLabel="Logo de Facet"
              />
            </View>
            <Text style={styles.title}>
              {translateText("Correo Electrónico", userLang)}
            </Text>

            <View style={[
              styles.inputContainer,
              emailFocused && styles.inputContainerFocused
            ]}>
              <MaterialCommunityIcons name='account' style={styles.icon} />
              <TextInput
                style={styles.inputWithIcon}
                placeholder={translateText("Correo Electrónico", userLang)}
                placeholderTextColor={LOGIN_SCREEN_COLORS.TEXT_PLACEHOLDER}
                onChangeText={setUsername}
                value={username}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="off"
                textContentType="none"
                accessibilityLabel="Campo de correo electrónico"
                accessibilityHint="Introduce tu correo electrónico"
                editable={!isLoading}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
              />
            </View>
            <Text style={styles.title}>
              {translateText("Contraseña", userLang)}
            </Text>

            <View style={[
              styles.inputContainer,
              passwordFocused && styles.inputContainerFocused
            ]}>
              <MaterialCommunityIcons name='lock' style={styles.icon} />
              <TextInput
                style={styles.inputWithIcon}
                placeholder={String(translateText("Contraseña", userLang))}
                placeholderTextColor={LOGIN_SCREEN_COLORS.TEXT_PLACEHOLDER}
                onChangeText={setPassword}
                value={password}
                secureTextEntry
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="new-password"
                textContentType="none"
                accessibilityLabel="Campo de contraseña"
                accessibilityHint="Introduce tu contraseña"
                editable={!isLoading}
                onSubmitEditing={handleSubmit}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
              />
            </View>
            <TouchableOpacity
              style={[styles.button, isLoading && styles.buttonDisabled]}
              onPress={handleSubmit}
              disabled={isLoading}
              accessibilityLabel="Botón de iniciar sesión"
              accessibilityHint="Pulsa para iniciar sesión"
              accessibilityRole="button">
              {!isLoading && (
                <Text style={styles.buttonText}>
                  {translateText("Iniciar sesión", userLang)}
                </Text>
              )}
              {isLoading && (
                <ActivityIndicator
                  size='small'
                  color={LOGIN_SCREEN_COLORS.TEXT_WHITE}
                  style={{ marginLeft: 10 }}
                />
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ImageBackground>
  );
}

const getStyles = (screenWidth, screenHeight) => {
  const isLargeScreen = screenWidth >= 1200;
  const isMediumScreen = screenHeight >= 670;
  const logoSize = isLargeScreen ? LOGIN_CONFIG.LOGO_SIZES.large : LOGIN_CONFIG.LOGO_SIZES.normal;

  return StyleSheet.create({
    background: {
      flex: 1,
      width: "100%",
      height: "100%",
      backgroundColor: LOGIN_SCREEN_COLORS.BACKGROUND_DARK,
    },
    keyboardAvoidingView: {
      flex: 1,
    },
    scrollViewContent: {
      flexGrow: 1,
      justifyContent: "center",
    },
    loginContainer: {
      width: isLargeScreen ? "80%" : "90%",
      maxWidth: 400,
      backgroundColor: "rgba(255, 255, 255, 0.55)",
      padding: 24,
      borderRadius: 16,
      alignItems: "center",
      alignSelf: isLargeScreen ? "flex-start" : "center",
      marginTop: isMediumScreen ? 50 : 25,
      marginLeft: isLargeScreen ? 150 : 0,
      shadowColor: "#000",
      shadowOffset: {
        width: 0,
        height: 8,
      },
      shadowOpacity: 0.2,
      shadowRadius: 16,
      elevation: 12,
      // Backdrop blur para web (si está disponible)
      ...(Platform.OS === "web" && {
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }),
    },
    logoContainer: {
      padding: 0,
      borderRadius: 50,
      marginBottom: 16,
    },
    logo: {
      width: logoSize.width,
      height: logoSize.height,
    },
    inputContainer: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: LOGIN_SCREEN_COLORS.BACKGROUND_LIGHT,
      borderRadius: 12,
      marginBottom: 12,
      borderWidth: 1.5,
      borderColor: LOGIN_SCREEN_COLORS.BORDER_LIGHT,
      paddingHorizontal: 14,
      width: "100%",
      shadowColor: "#000",
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowOpacity: 0.05,
      shadowRadius: 3,
      elevation: 2,
    },
    inputContainerFocused: {
      borderColor: LOGIN_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY,
      borderWidth: 2,
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    icon: {
      color: LOGIN_SCREEN_COLORS.TEXT_PRIMARY,
      fontSize: 22,
      marginRight: 12,
      opacity: 0.7,
    },
    inputWithIcon: {
      flex: 1,
      color: LOGIN_SCREEN_COLORS.TEXT_PRIMARY,
      paddingVertical: 12,
      fontSize: 15,
      // Desactivar el estilo amarillo del autofill en web
      ...(Platform.OS === "web" && {
        WebkitTextFillColor: LOGIN_SCREEN_COLORS.TEXT_PRIMARY,
        WebkitBoxShadow: `0 0 0px 1000px ${LOGIN_SCREEN_COLORS.BACKGROUND_LIGHT} inset`,
        boxShadow: `0 0 0px 1000px ${LOGIN_SCREEN_COLORS.BACKGROUND_LIGHT} inset`,
        transition: "all 0.2s ease",
        outline: "none",
      }),
    },
    title: {
      color: LOGIN_SCREEN_COLORS.TEXT_PRIMARY,
      fontSize: isLargeScreen ? 14 : 13,
      marginBottom: 6,
      marginTop: 4,
      fontWeight: "600",
      alignSelf: "flex-start",
      letterSpacing: 0.3,
    },
    input: {
      backgroundColor: LOGIN_SCREEN_COLORS.BACKGROUND_LIGHT,
      color: LOGIN_SCREEN_COLORS.TEXT_PRIMARY,
      paddingVertical: 10,
      paddingHorizontal: 15,
      borderRadius: 5,
      marginBottom: 15,
      borderWidth: 1,
      borderColor: LOGIN_SCREEN_COLORS.BORDER_LIGHT,
      fontSize: 16,
      width: "100%",
    },
    button: {
      backgroundColor: LOGIN_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY,
      borderRadius: 12,
      paddingVertical: 14,
      paddingHorizontal: 20,
      alignItems: "center",
      justifyContent: "center",
      marginTop: 16,
      width: "100%",
      shadowColor: "#000",
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.25,
      shadowRadius: 8,
      elevation: 6,
    },
    buttonDisabled: {
      backgroundColor: LOGIN_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY,
      opacity: 0.7,
    },
    buttonText: {
      color: LOGIN_SCREEN_COLORS.TEXT_WHITE,
      fontSize: 15,
      fontWeight: "600",
      textAlign: "center",
      letterSpacing: 0.5,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: LOGIN_SCREEN_COLORS.BACKGROUND_DARK,
    },
  });
};
